import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { SidePanel } from '@carbon/ibm-products';
import {
  DatePicker,
  DatePickerInput,
  InlineNotification,
  TextInput,
  Toggle,
} from '@carbon/react';
import { getDateString, parseUTCdateTime } from 'utils/dates';
import type { OverviewResponse } from './types-overview';
import { editOverviewAtom, overviewUrl } from './atoms/overview';
import {
  EDITABLE_OVERVIEW_FIELDS,
  fieldInputValue,
  type OverviewFieldDef,
} from './overview-utils';
import styles from 'pages/afe-catalog/structuring/identification.module.scss';

interface Props {
  open: boolean;
  documentId: number | string;
  detail: OverviewResponse;
  onClose: () => void;
  onSaved: () => void;
}

const blank = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

// Mirror of DO's overview-edit-panel: an @carbon/ibm-products SidePanel over a
// flat working copy, submitting a snake_case { overview: {...} } body. RC adds a
// Toggle for the boolean D3 gap fields. selectorPageContent targets the RC shell
// class so the slide-in push-anchor resolves.
export const OverviewEditPanel: FC<Props> = ({
  open,
  documentId,
  detail,
  onClose,
  onSaved,
}) => {
  const editOverview = useSetAtom(editOverviewAtom);
  const [values, setValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable in the panel = FE-editable AND not BE-locked (fieldEditable !== false).
  const panelFields = EDITABLE_OVERVIEW_FIELDS.filter(
    (def) => detail.fieldEditable[def.key] !== false
  );

  useEffect(() => {
    if (!open) {
      setError(null);
      return;
    }
    const seeded: Record<string, string> = {};
    panelFields.forEach((def) => {
      seeded[def.key] = fieldInputValue(def, detail.fields);
    });
    setValues(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detail]);

  const setValue = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    // Submit only fields the user actually changed vs the seeded baseline. The
    // backend logs an OverviewEdited event and refreshes derived totals per
    // changed field, so sending the full set would flag untouched fields as
    // edited and flip the section status on a no-op save.
    const overview: Record<string, string | null> = {};
    panelFields.forEach((def) => {
      const seeded = fieldInputValue(def, detail.fields);
      const current = values[def.key] ?? '';
      if (current === seeded) return;
      overview[def.requestKey] = blank(current);
    });

    // No-op edit → close without firing an edit event.
    if (Object.keys(overview).length === 0) {
      onClose();
      return;
    }

    setPending(true);
    setError(null);

    editOverview({
      url: overviewUrl(documentId),
      method: 'PATCH',
      data: { overview },
      onSuccess: () => {
        setPending(false);
        onSaved();
      },
      onError: (m) => {
        setPending(false);
        setError(m ?? 'Failed to save overview.');
      },
    });
  };

  const renderField = (def: OverviewFieldDef) => {
    const value = values[def.key] ?? '';

    if (def.input === 'boolean') {
      return (
        <Toggle
          key={def.key}
          id={`rc-overview-${def.key}`}
          labelText={def.label}
          labelA="No"
          labelB="Yes"
          toggled={value === 'true'}
          onToggle={(checked: boolean) =>
            setValue(def.key, checked ? 'true' : 'false')
          }
        />
      );
    }

    if (def.input === 'date') {
      return (
        <DatePicker
          key={def.key}
          datePickerType="single"
          dateFormat="m/d/Y"
          value={value ? parseUTCdateTime(value) : undefined}
          onChange={(dates: Date[]) =>
            setValue(def.key, dates[0] ? getDateString(dates[0]) : '')
          }
        >
          <DatePickerInput
            id={`rc-overview-${def.key}`}
            labelText={def.label}
            placeholder="mm/dd/yyyy"
          />
        </DatePicker>
      );
    }

    return (
      <TextInput
        key={def.key}
        id={`rc-overview-${def.key}`}
        labelText={def.label}
        type={def.input === 'number' ? 'number' : 'text'}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setValue(def.key, e.target.value)
        }
      />
    );
  };

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Edit Royalty Check Overview"
      subtitle="Update any field below and click Save to apply your changes."
      slideIn
      selectorPageContent=".rc-structuring-shell"
      placement="right"
      size="md"
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: pending ? 'Saving…' : 'Save',
          disabled: pending,
          onClick: handleSave,
        },
      ]}
    >
      <div className={`${styles.panelForm} ${styles.panelLightFields}`}>
        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        )}
        <div className={styles.panelColumn}>{panelFields.map(renderField)}</div>
      </div>
    </SidePanel>
  );
};
